import { Box, Typography } from '@mui/material';

import { DrawerFormPreview } from '../../../../common/components';
import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_TRACEABILITY_OPTIONS,
  getOptionLabel,
} from '../../constants/supplierLedgerConstants';
import { RECEIPT_TYPE_OPTIONS } from '../../constants/supplierLedgerUiConstants';
import { calculateAmountLine } from '../../utils/supplierLedgerHelpers';
import { formatCurrencyAmount } from '../../utils/supplierLedgerFormatters';
import { AmountPanel } from './SupplierLedgerFormFields';
import InvoicePreviewField from './InvoicePreviewField';

const getLineClassificationLabel = (line) => [
  getOptionLabel(COST_BEHAVIOR_OPTIONS, line.cost_behavior),
  getOptionLabel(COST_TRACEABILITY_OPTIONS, line.cost_nature || line.cost_traceability),
  getOptionLabel(COST_AREA_OPTIONS, line.cost_area),
].filter(Boolean).join(' / ');

const getConceptSummary = (amountLines) => Array.from(new Set(
  amountLines.map((line) => line.movement_concept_label).filter(Boolean),
)).join(', ');

const InvoiceHeader = ({
  amountLines,
  form,
  providerName,
  receiptLabel,
  receiptLetter,
  receiptNumber,
}) => (
  <>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 104px minmax(0, 1fr)' },
        alignItems: 'stretch',
        minWidth: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ p: 2, minWidth: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 900, lineHeight: 1, textTransform: 'uppercase', mb: 1 }}>
          Proveedor emisor
        </Typography>
        <Typography
          variant="h6"
          sx={{ color: 'text.primary', fontWeight: 900, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.75 }}
          title={providerName || 'Proveedor sin seleccionar'}
        >
          {providerName || 'Proveedor sin seleccionar'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600 }}>
          Factura recibida para cargar en cuenta corriente
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderLeft: { xs: 0, sm: '2px solid' },
          borderRight: { xs: 0, sm: '2px solid' },
          borderTop: { xs: '1px solid', sm: 0 },
          borderBottom: { xs: '1px solid', sm: 0 },
          borderColor: 'primary.main',
          p: 1.25,
          minHeight: 110,
          backgroundColor: 'background.formTables',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, lineHeight: 1 }}>
          TIPO
        </Typography>
        <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 900, lineHeight: 1, my: 0.25 }}>
          {receiptLetter}
        </Typography>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 900, lineHeight: 1.1, textAlign: 'center' }}>
          {receiptLabel}
        </Typography>
      </Box>

      <Box sx={{ p: 2, minWidth: 0, textAlign: { xs: 'left', sm: 'right' } }}>
        <Typography
          variant="caption"
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.35,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            color: 'primary.main',
            fontWeight: 900,
            lineHeight: 1,
            mb: 0.75,
          }}
        >
          COMPROBANTE
        </Typography>
        <Typography
          variant="h6"
          sx={{ color: 'text.primary', fontWeight: 900, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={receiptNumber}
        >
          N° {receiptNumber}
        </Typography>
        <Box sx={{ mt: 1.25, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          <InvoicePreviewField label="Emisión" value={form.invoice_date || '-'} highlight />
          <InvoicePreviewField label="Vencimiento" value={form.due_date || '-'} highlight />
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        p: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
        columnGap: 2,
        rowGap: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <InvoicePreviewField label="Receptor" value="Constructora Colonial" highlight />
      <InvoicePreviewField label="Concepto" value={getConceptSummary(amountLines) || '-'} />
      <InvoicePreviewField label="Moneda" value={form.currency} />
    </Box>
  </>
);

const InvoicePreviewLines = ({ amountLines, form }) => (
  <Box sx={{ p: 2 }}>
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', sm: 'grid' },
          gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr) 90px minmax(0, 1fr) minmax(0, 1fr)',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.formTables',
        }}
      >
        {['Detalle', 'Total con IVA', 'IVA %', 'Neto', 'IVA'].map((label) => (
          <Typography key={label} variant="caption" sx={{ color: 'primary.main', fontWeight: 800 }}>
            {label}
          </Typography>
        ))}
      </Box>

      {amountLines.map((line, index) => {
        const lineAmounts = calculateAmountLine(line);
        return (
          <Box
            key={line.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.5fr) minmax(0, 1fr) 90px minmax(0, 1fr) minmax(0, 1fr)' },
              gap: 1,
              px: 1.5,
              py: 1,
              borderBottom: index === amountLines.length - 1 ? 0 : '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <InvoicePreviewField
              label={`Importe ${index + 1}`}
              value={line.description || line.movement_concept_label || 'Detalle de importe'}
              highlight
            />
            <InvoicePreviewField label="Total con IVA" value={formatCurrencyAmount(lineAmounts.gross, form.currency)} highlight />
            <InvoicePreviewField label="IVA %" value={`${String(line.vat_rate).replace('.', ',')}%`} />
            <InvoicePreviewField label="Neto" value={formatCurrencyAmount(lineAmounts.net, form.currency)} />
            <InvoicePreviewField label="IVA" value={formatCurrencyAmount(lineAmounts.vat, form.currency)} />
          </Box>
        );
      })}
    </Box>
  </Box>
);

const InvoicePreviewTotals = ({ amountTotals, form }) => (
  <Box
    sx={{
      p: 2,
      pt: 0,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
      gap: 1.25,
    }}
  >
    <AmountPanel label="Neto" value={formatCurrencyAmount(amountTotals.net, form.currency)} />
    <AmountPanel label="IVA" value={formatCurrencyAmount(amountTotals.vat, form.currency)} />
    <AmountPanel label="Percepciones" value={formatCurrencyAmount(amountTotals.perceptions, form.currency)} />
    <AmountPanel label="Total" value={formatCurrencyAmount(amountTotals.total, form.currency)} />
  </Box>
);

const InvoiceInternalData = ({
  amountLines,
  currentFileName,
  file,
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'background.paper',
      p: 1.5,
    }}
  >
    <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 800, mb: 1 }}>
      Datos internos
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {amountLines.map((line, index) => (
        <Box
          key={line.id}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '80px repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
          }}
        >
          <InvoicePreviewField label="Importe" value={index + 1} highlight />
          <InvoicePreviewField label="Concepto" value={line.movement_concept_label || '-'} />
          <InvoicePreviewField label="Cuenta contable" value={line.accounting_account_label || '-'} />
          <InvoicePreviewField label="Clasificación" value={getLineClassificationLabel(line) || '-'} />
        </Box>
      ))}
      <InvoicePreviewField label="Adjunto" value={file?.name || currentFileName || 'Sin archivo adjunto'} />
    </Box>
  </Box>
);

export const InvoicePreview = ({
  form,
  providerName,
  file,
  currentFileName = '',
  amountLines,
  amountTotals,
}) => {
  const receiptLabel = getOptionLabel(RECEIPT_TYPE_OPTIONS, form.receipt_type) || form.receipt_type || 'Comprobante';
  const receiptNumber = form.receipt_number || 'Sin número';
  const receiptType = form.receipt_type || '-';
  const receiptLetter = receiptType.length > 1 ? receiptType.replace(/[^A-Z]/g, '') : receiptType;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <DrawerFormPreview bodySx={{ p: 0, gap: 0 }}>
        <InvoiceHeader
          amountLines={amountLines}
          form={form}
          providerName={providerName}
          receiptLabel={receiptLabel}
          receiptLetter={receiptLetter}
          receiptNumber={receiptNumber}
        />
        <InvoicePreviewLines amountLines={amountLines} form={form} />
        <InvoicePreviewTotals amountTotals={amountTotals} form={form} />
      </DrawerFormPreview>

      <InvoiceInternalData
        amountLines={amountLines}
        currentFileName={currentFileName}
        file={file}
      />
    </Box>
  );
};

export { InvoicePreviewField };
